/**
 * By-reseller section under the fleet table (TMMPDM-0020): one money column group per currency
 * present — values from each row's own `totals[]`, `—` where that reseller has none.
 */
import {
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useCopy } from '@/hooks/useCopy';
import { Amount } from '@/components/ui';
import type { FleetData } from './useFleetDashboard';

interface ByResellerTableProps {
  data: FleetData | null;
  locale: string;
}

export function ByResellerTable({ data, locale }: ByResellerTableProps) {
  const { t } = useCopy();
  if (!data || data.dashboard.byReseller.length === 0) return null;

  const currencies = [
    ...new Set(data.dashboard.byReseller.flatMap((row) => row.totals.map((e) => e.currency))),
  ].sort();

  return (
    <>
      <Typography variant="h2" component="h2" gutterBottom sx={{ mt: 3 }}>
        {t('dashboard.byReseller.title', 'By reseller')}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label={t('dashboard.byReseller.tableAria', 'Fleet by reseller')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('dashboard.byReseller.col.reseller', 'Reseller')}</TableCell>
              <TableCell align="right">
                {t('dashboard.byReseller.col.tenants', 'Tenants')}
              </TableCell>
              <TableCell align="right">
                {t('dashboard.byReseller.col.subscribers', 'Active subscribers')}
              </TableCell>
              {currencies.map((currency) => (
                <TableCell key={`${currency}-retail`} align="right">
                  {t('dashboard.byReseller.col.retail', 'Retail')} ({currency})
                </TableCell>
              ))}
              {currencies.map((currency) => (
                <TableCell key={`${currency}-platform`} align="right">
                  {t('dashboard.byReseller.col.platform', 'Platform share')} ({currency})
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.dashboard.byReseller.map((row) => {
              const key = row.resellerId === null ? 'none' : String(row.resellerId);
              const name =
                row.resellerId === null
                  ? t('dashboard.byReseller.noReseller', 'No reseller')
                  : (row.resellerName ?? `#${row.resellerId}`);
              return (
                <TableRow key={key} hover>
                  <TableCell>
                    {row.resellerId === null ? (
                      <Typography variant="body2">{name}</Typography>
                    ) : (
                      <Link
                        component={RouterLink}
                        to={`/partners/${row.resellerId}`}
                        variant="body2"
                        underline="hover"
                      >
                        {name}
                      </Link>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Amount count={row.tenantCount} locale={locale} />
                  </TableCell>
                  <TableCell align="right">
                    <Amount count={row.counts.subscribers.active} locale={locale} />
                  </TableCell>
                  {currencies.map((currency) => {
                    const entry = row.totals.find((e) => e.currency === currency);
                    return (
                      <TableCell key={`${key}-r-${currency}`} align="right">
                        {entry ? (
                          <Amount
                            minor={entry.revenue.retailMinor}
                            currency={currency}
                            locale={locale}
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    );
                  })}
                  {currencies.map((currency) => {
                    const entry = row.totals.find((e) => e.currency === currency);
                    return (
                      <TableCell key={`${key}-p-${currency}`} align="right">
                        {entry ? (
                          <Amount
                            minor={entry.revenue.platformShareMinor}
                            currency={currency}
                            locale={locale}
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
